import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { Button } from '@/components/Elements';
import { DEFAULT_LANG } from '@/config';
import { DEFAULT_USER_LANGUAGE_FOR_CONTACT } from '@/config/appConstants';
import { MobileToken, WRONG_TOKEN } from '@/features/mobileToken';
import { AppLanguages, BaseFormEntity, UserData } from '@/types';
import { checkPhone, renderField, renderSelect } from '@/utils/forms';
import { isAdminUser } from '@/utils/helpers';

export type FormPhoneValidationProps = {
  className?: string;
  initialValues: iFormPhoneValidationFields | undefined;
  onSubmit: (values: iFormPhoneValidationFields) => void;
  mobileToken: MobileToken;
  userData: UserData;
};

export type iFormPhoneValidationFields = {
  groupId: string;
  lang: string;
  phone: string;
} & BaseFormEntity;

const initialValuesDefault: iFormPhoneValidationFields = {
  groupId: '',
  lang: DEFAULT_USER_LANGUAGE_FOR_CONTACT,
  phone: '',
  formName: 'PhoneValidation',
};

export const FormPhoneValidation = ({
  className,
  initialValues,
  onSubmit,
  mobileToken,
  userData,
}: FormPhoneValidationProps) => {
  const { t, i18n } = useTranslation();

  initialValuesDefault.groupId = userData.groupId;
  initialValuesDefault.phone = mobileToken.phone || '';
  initialValuesDefault.lang = i18n.language || 'es';

  const defaultValidationSchema = {
    phone: Yup.string()
      .min(3, `${t('Forms.minErrorChars')} 3 ${t('Forms.chars')}`)
      .test('test-phone', t('Forms.invalidFormat'), checkPhone)
      .required(t('Forms.required')),
    groupId: Yup.string().required(t('Forms.required')),
  };

  const MainForm = () => {
    const onFormSubmit = (values: iFormPhoneValidationFields) => {
      onSubmit && onSubmit(values);
    };

    const formikData = {
      initialValues: { ...initialValuesDefault, ...initialValues },
      validationSchema: Yup.object(defaultValidationSchema),
      onSubmit: onFormSubmit,
    };

    const formik: any = useFormik(formikData);

    return (
      <form onSubmit={formik.handleSubmit} className={className}>
        <h3 className="md__payments__form--h3">{t('Forms.PhoneValidation.title')}</h3>
        <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
          {renderSelect(formik, 'phone', {
            customClasses: 'w-full md:w-1/2',
            type: 'phone',
            required: true,
            renderLabel: (item: { name: string }) => item.name,
            defaultCountry: i18n.language.toUpperCase(),
            userLanguage: (i18n.language || DEFAULT_LANG) as AppLanguages,
          })}
        </div>
        {renderField(formik, 'groupId', { type: 'hidden' })}
        {renderField(formik, 'lang', { type: 'hidden', value: i18n.language })}
        <div className="mt-10 mb-3 flex justify-center lg:justify-between flex-wrap">
          <div className="flex items-center mb-10 lg:mb-0 flex-wrap justify-center lg:justify-start w-full lg:w-auto ">
            <Button
              variant="primary"
              type="submit"
              size="free"
              className="py-5 lg:py-3 w-full lg:w-[140px]"
            >
              {getActionButton(mobileToken)}
            </Button>
          </div>
        </div>
      </form>
    );
  };

  const getActionButton = (mobileToken: MobileToken): string => {
    return typeof mobileToken.deliveries === 'undefined' ||
      parseInt(mobileToken.deliveries, 10) === 0
      ? t('Forms.PhoneValidation.send')
      : t('Forms.PhoneValidation.retry');
  };

  const isUsed = (mobileToken: MobileToken, userData: UserData): boolean => {
    if (isAdminUser(userData)) {
      return true;
    }
    if (mobileToken.usedAt) {
      return true;
    }

    return false;
  };

  const isBlocked = (mobileToken: MobileToken, userData: UserData): boolean => {
    if (isAdminUser(userData)) {
      return true;
    }
    if (
      typeof mobileToken.deliveries !== 'undefined' &&
      parseInt(mobileToken.deliveries, 10) >= 3
    ) {
      return true;
    }

    if (typeof mobileToken.tries !== 'undefined' && parseInt(mobileToken.tries, 10) >= 5) {
      return true;
    }

    return false;
  };

  const getError = (mobileToken: MobileToken): number => {
    if (typeof mobileToken.hasError !== 'undefined') {
      return mobileToken.hasError;
    }

    return 0;
  };

  const used: boolean = isUsed(mobileToken, userData);
  const blocked: boolean = isBlocked(mobileToken, userData);
  const errorToken: number = getError(mobileToken);

  return (
    <div>
      <div className="w-full m-auto overflow px-4 py-0 lg:py-6">
        {used && <h3 className="md__payments__form--h3">{t('Forms.tokenChecked')}</h3>}

        {errorToken == WRONG_TOKEN && (
          <h3 className="md__payments__form--h3">{t('Forms.cantSendSmsError')}</h3>
        )}

        {blocked && !used && (
          <h3 className="md__payments__form--h3">{t('Forms.blockBillingDetails')}</h3>
        )}

        {!used && !blocked && <MainForm />}
      </div>
    </div>
  );
};
